/**
 * THE DESTINATIONS.
 *
 * One Google link, one Facebook page, one set of handles — for the whole
 * company, because the customer is reviewing Southern Recognition, not their
 * own school. That is the thing worth stating on the screen: people reach for
 * a per-account link here and there is no such thing.
 *
 * Nothing is derived. A Google review URL carries a Place ID that cannot be
 * guessed from a business name, so a blank field stays blank and shows the
 * three steps for filling it in. A fabricated one would send every customer SR
 * has to a stranger's review box.
 */
import { useEffect, useState } from 'react'
import { api } from '../../lib/api.js'
import s from './reviews.module.css'

const FIELDS = [
  { key: 'googleReviewUrl', label: 'Google review link', kind: 'url',
    hint: 'Google Business Profile → “Ask for reviews” → copy link. It looks like https://g.page/r/…/review and opens the review box directly.' },
  { key: 'facebookReviewUrl', label: 'Facebook reviews', kind: 'url',
    hint: 'Your SR page → Reviews → copy the URL.' },
  { key: 'instagramHandle', label: 'Instagram handle', kind: 'handle',
    hint: 'What a customer types to tag you, e.g. @southernrecognition.' },
  { key: 'instagramUrl', label: 'Instagram profile', kind: 'url', hint: '' },
  { key: 'tiktokHandle', label: 'TikTok handle', kind: 'handle', hint: '' },
  { key: 'tiktokUrl', label: 'TikTok profile', kind: 'url', hint: '' },
  { key: 'xHandle', label: 'X handle', kind: 'handle', hint: '' },
  { key: 'xUrl', label: 'X profile', kind: 'url', hint: '' },
]

export default function LinksPanel({ onSaved }) {
  const [data, setData] = useState(null)
  const [form, setForm] = useState({})
  const [err, setErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api.reviewLinks()
      .then((d) => { setData(d); setForm(d.links || {}) })
      .catch((e) => setErr(e?.message || 'Could not load.'))
  }, [])

  const save = async () => {
    setSaving(true); setErr(null); setSaved(false)
    try {
      const r = await api.reviewLinksSave(form)
      setForm(r.links)
      setSaved(true)
      onSaved && onSaved()
      window.setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setErr(e?.body?.reason || e?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  if (err && !data) return <div className={s.wrap}><div className={s.empty}>{err}</div></div>
  if (!data) return <div className={s.wrap}><div className={s.empty}>Loading…</div></div>

  const editable = data.editable

  return (
    <div className={s.wrap}>
      <p className={s.rule}>
        These are SR’s own destinations and they are identical on every account — a
        customer leaving a Google review is reviewing Southern Recognition, not their
        school. What changes per account is the message wrapped around them.
      </p>

      {data.missing?.length > 0 && (
        <div className={s.warn}>
          <strong>{data.missing.length} destination{data.missing.length === 1 ? '' : 's'} not set.</strong>{' '}
          Until the Google link is in, the email drafts leave it out rather than guessing
          an address — a wrong Place ID would send every customer to somebody else’s
          review box.
        </div>
      )}

      {!editable && (
        <div className={s.warn}>
          These go out to every customer SR has, so one wrong paste is a company-wide
          error. Executive-set only — ask Brian, Rob or John.
        </div>
      )}

      <div className={s.fieldGrid}>
        {FIELDS.map((f) => (
          <label key={f.key} className={s.field}>
            <span className={s.fieldLabel}>{f.label}</span>
            <input
              className={s.input}
              type="text"
              value={form[f.key] || ''}
              disabled={!editable}
              placeholder={f.kind === 'handle' ? '@southernrecognition' : 'https://…'}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
            {f.hint && <span className={s.fieldHint}>{f.hint}</span>}
            {form[f.key] && f.kind === 'url' && (
              <a className={s.fieldHint} href={form[f.key]} target="_blank" rel="noopener noreferrer">
                Open it and check it lands where you expect
              </a>
            )}
          </label>
        ))}
      </div>

      {err && <div className={s.warn} style={{ marginTop: 16 }}>{err}</div>}

      {editable && (
        <div className={s.actions}>
          <button type="button" className={s.btn} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : saved ? 'Saved' : 'Save destinations'}
          </button>
        </div>
      )}

      <div className={s.note}>
        <strong>The six types, and they are not six flavours of one thing.</strong>
        <ul className={s.typeList}>
          {Object.entries(data.types || {}).map(([k, t]) => (
            <li key={k}>
              <b>{t.label}</b> <span className={s.chip}>{t.kind}</span> {t.why}
            </li>
          ))}
        </ul>
        A <b>platform</b> review is a stranger-facing verdict typed into a search engine.
        A <b>social</b> post is the customer celebrating their own moment and tagging us —
        it was going to happen anyway, and the only ask is that we are in it.
      </div>
    </div>
  )
}
